import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import Select from 'react-select'

import Search from 'pages/Parts/Search'
import Pagination from 'pages/Parts/Pagination'

import { PGET, POST } from 'helpers/api'
import { formatMoney, formatDateWithTime } from 'helpers/helpers'
import * as XLSX from 'xlsx'

function Index() {
	const URL = '/services/admin/api/unconfirmed-payments-pageList'
	const { t } = useTranslation()

	const authorities = JSON.parse(localStorage.getItem('authorities')) || [];
	const isAdmin = authorities.includes('ROLE_ADMIN');


	const settings = useSelector(state => state.settings)
	const [pageCount, setPageCount] = useState(0)
	const [totalCount, setTotalCount] = useState(0)
	const [data, setData] = useState([])
	const [pageSize, setPageSize] = useState(20)

	async function confirmPayments(id) {
		if (!isAdmin) return;
		var response = await POST('/services/admin/api/confirm-payment/' + id, {}, { loader: true })

		if (response) {
			getData()
		}
	}

	async function getData() {
		const response = await PGET('/services/admin/api/unconfirmed-payments-pageList', { size: pageSize }, { loader: true })
		if (response) {
			setTotalCount(Math.ceil(response.headers['x-total-count']))
			setPageCount(Math.ceil(response.headers['x-total-count'] / pageSize))
			setData(response.data);
		}
	}

	async function paginate(data) {
		const response = await PGET('/services/admin/api/unconfirmed-payments-pageList', { page: data.selected, size: pageSize }, { loader: true })
		var updatedData = response.data
		setData(updatedData);
	}

	async function search(data, searchValue) {
		var updatedData = data
		setData(updatedData);
		if (searchValue.length > 0) {
			setPageCount(0)
		}
	}

	useEffect(() => {
		getData()
		
	}, [pageSize]) // eslint-disable-line react-hooks/exhaustive-deps

	function handlePageSizeChange(selectedOption) {
		if (selectedOption.value === 'all') {
			// Export all data directly and keep current page size selection
			exportAllToExcel();
			// Don't change the pageSize state, so the selector stays on current value
		} else {
			setPageSize(selectedOption.value)
		}
	}

	const pageSizeOptions = [
		{ value: 20, label: '20' },
		{ value: 50, label: '50' },
		{ value: 100, label: '100' },
		{ value: 500, label: '500' },
		{ value: 1000, label: '1000' },
		{ value: 'all', label: t('ALL') }
	]

	// Helper function to safely format dates
	const safeFormatDateWithTime = (dateValue) => {
		if (!dateValue) return '';
		try {
			return formatDateWithTime(dateValue);
		} catch (error) {
			console.warn('Invalid date value:', dateValue, error);
			return '';
		}
	}

	// Excel export function for current page
	function exportToExcel() {
		// Prepare data for Excel
		const excelData = data.map((item, index) => ({
			'№': index + 1,
			[t('ID')]: item.id || '',
			[t('SERVER')]: item.server || '',
			[t('OWNER')]: item.login ? `${item.login} (POS ID: ${item.posId})` : '',
			[t('CUSTOMER_CREATOR')]: item.createdBy || '',
			[t('DATE_OF_CREATION')]: safeFormatDateWithTime(item.createdDate),
			[t('AMOUNT')]: item.amountIn ? formatMoney(item.amountIn) : '',
			[t('MONTHLY_PAYMENT')]: item.monthlyPayment ? formatMoney(item.monthlyPayment) : '',
			[t('PAID_FROM')]: item.paidFrom || '',
			[t('PAID_UNTIL')]: item.paidUntil || '',
			[t('ACTIVATE')]: item.status == 1 ? t('CONFIRMED') : t('PENDING')
		}))

		// Create workbook and worksheet
		const ws = XLSX.utils.json_to_sheet(excelData)
		const wb = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(wb, ws, t('CONFIRM_PAYMENTS'))

		// Generate Excel file and download
		XLSX.writeFile(wb, `${t('CONFIRM_PAYMENTS')}_${new Date().toLocaleDateString()}.xlsx`)
	}

	// Excel export function for all data
	async function exportAllToExcel() {
		try {
			// Get all data without pagination
			const response = await PGET('/services/admin/api/unconfirmed-payments-pageList', { size: totalCount }, { loader: true })
			
			if (response && response.data) {
				// Prepare all data for Excel
				const excelData = response.data.map((item, index) => ({
					'№': index + 1,
					[t('ID')]: item.id || '',
					[t('SERVER')]: item.server || '',
					[t('OWNER')]: item.login ? `${item.login} (POS ID: ${item.posId})` : '',
					[t('CUSTOMER_CREATOR')]: item.createdBy || '',
					[t('DATE_OF_CREATION')]: safeFormatDateWithTime(item.createdDate),
					[t('AMOUNT')]: item.amountIn ? formatMoney(item.amountIn) : '',
					[t('MONTHLY_PAYMENT')]: item.monthlyPayment ? formatMoney(item.monthlyPayment) : '',
					[t('PAID_FROM')]: item.paidFrom || '',
					[t('PAID_UNTIL')]: item.paidUntil || '',
					[t('ACTIVATE')]: item.status == 1 ? t('CONFIRMED') : t('PENDING')
				}))

				// Create workbook and worksheet
				const ws = XLSX.utils.json_to_sheet(excelData)
				const wb = XLSX.utils.book_new()
				XLSX.utils.book_append_sheet(wb, ws, t('CONFIRM_PAYMENTS'))

				// Generate Excel file and download
				XLSX.writeFile(wb, `${t('CONFIRM_PAYMENTS')}_${t('ALL')}_${new Date().toLocaleDateString()}.xlsx`)
			}
		} catch (error) {
			console.error('Error exporting all data:', error)
		}
	}

	return (
		<>
			<div className="page-header d-flex justify-content-between mb-2">
				<h4 className="vertical-center">
					{t('CONFIRM_PAYMENTS')} ({totalCount})
				</h4>
				<div className="d-flex">
					<button className="btn-rounded btn btn-primary me-2" onClick={exportToExcel}>
						<i className="uil uil-file-export me-2"></i>
						Excel
					</button>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex mb-3">
						<div className="me-2">
							<Search url={URL} search={search} getData={getData}></Search>
						</div>
						<div className="me-2" style={{ minWidth: '120px' }}>
							<Select
								options={pageSizeOptions}
								value={pageSizeOptions.find(option => option.value === pageSize)}
								onChange={handlePageSizeChange}
								placeholder="Page Size"
								isSearchable={false}
							/>
						</div>
					</div>
					<div className="table-responsive">
						<table className="table mb-0">
							<thead>
								<tr>
									<th>{t('ID')}</th>
									<th>{t('SERVER')}</th>
									<th>{t('OWNER')}</th>
									<th className="text-center">{t('CUSTOMER_CREATOR')}</th>
									<th className="text-center">{t('DATE_OF_CREATION')}</th>
									<th className="text-center">{t('AMOUNT')}</th>
									<th className="text-center">{t('MONTHLY_PAYMENT')}</th>
									<th className="text-center">{t('PAID_FROM')}</th>
									<th className="text-center">{t('PAID_UNTIL')}</th>
									<th className="text-center">{t('ACTIVATE')}</th>
								</tr>
							</thead>
							<tbody>
								{data.map((item, index) => (
									<tr className={'tr-middle '} key={index} >
										<td>{item.id}</td>
										<td>{item.server}</td>
										<td>{item.login} (POS ID: {item.posId})</td>
										<td>{item.createdBy}</td>
										<td className="text-center">{formatDateWithTime(item.createdDate)}</td>
										<td className="text-center">{formatMoney(item.amountIn)}</td>
										<td className="text-center">{formatMoney(item.monthlyPayment)}</td>
										<td className="text-center">{item.paidFrom}</td>
										<td className="text-center">{item.paidUntil}</td>
										<td>
											<div className="d-flex justify-content-center">
												<div className="form-check form-switch form-switch-lg">
													<input type="checkbox" className="form-check-input"
														name="activated"
														checked={item.status == 1 ? true : false} disabled={!isAdmin || item.status == 1}
														onChange={(e) => confirmPayments(item.id)} />
												</div>
											</div>
										</td>
									</tr>
								))
								}
							</tbody>
						</table>
					</div>

					<Pagination pageCount={pageCount} paginate={paginate}></Pagination>
				</div>
			</div>
		</>
	)
}

export default Index
